import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Loader2,
  User,
  Calendar,
  TrendingUp,
  Wallet,
  FileDown,
  Inbox,
  Clock,
  Briefcase,
  Banknote,
  TrendingDown,
  Calculator,
} from "lucide-react";

import Layout from "@/components/layout/Layout";
import MainHeading from "@/components/common/MainHeading";
import Card from "@/components/common/Card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import CommonModal from "@/components/common/CommonModal";
import {
  DocumentPreview,
  type TDocumentInput,
} from "@/utility/documentPreview";

import { useCurrentUser } from "@/hooks/useCurrentUser";
import { PayslipService } from "@/services/Payslip/PayslipService";
import { ThrowError } from "@/api/callApi";
import type { IFilter } from "@/interface/common.interface";
import type { IPayslipOverviewResponse } from "@/interface/payslip.interface";
import {
  getCurrentPeriod,
  handleFormatAmount,
  showToastNotification,
} from "@/utility/helper";
import FullPageLoader from "@/components/common/FullPageLoader";
import SummaryCard from "@/components/common/SummaryCard";
import BreakdownTable from "@/components/common/BreakdownTable";

// Pay-cycle config
// cycle runs day-21 -> day-20 of next month (e.g. 2026-07-21 to 2026-08-20).
// Change this single constant if the cycle start day ever changes.
const CYCLE_START_DAY = 21;
const MIN_SELECTABLE_YEAR = 1990;
const MAX_SELECTABLE_YEAR = new Date().getFullYear() + 1;

const formatDateISO = (date: Date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const generateMonthOptionsForYear = (
  year: number,
  cycleStartDay: number = CYCLE_START_DAY,
) => {
  const options: { label: string; startDate: string; endDate: string }[] = [];
  for (let m = 0; m < 12; m++) {
    const start = new Date(year, m, cycleStartDay);
    const end = new Date(year, m + 1, cycleStartDay - 1);
    options.push({
      label: `${start.toLocaleString("default", { month: "short" })} ${start.getFullYear()} - ${end.toLocaleString("default", { month: "short" })} ${end.getFullYear()}`,
      startDate: formatDateISO(start),
      endDate: formatDateISO(end),
    });
  }
  return options;
};

const Payslip: React.FC = () => {
  const { userId } = useCurrentUser();

  const [overview, setOverview] = useState<IPayslipOverviewResponse | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState<TDocumentInput>(null);
  const [showPreview, setShowPreview] = useState(false);

  const [filter, setFilter] = useState<IFilter>({
    search: "",
    startDate: getCurrentPeriod().startDate,
    endDate: getCurrentPeriod().endDate,
  });

  const [selectedYear, setSelectedYear] = useState<number>(() =>
    filter.startDate
      ? new Date(filter.startDate).getFullYear()
      : new Date().getFullYear(),
  );

  const monthOptions = useMemo(
    () => generateMonthOptionsForYear(selectedYear),
    [selectedYear],
  );

  const selectedPeriodLabel = useMemo(() => {
    const option = monthOptions.find(
      (opt) =>
        opt.startDate === filter.startDate && opt.endDate === filter.endDate,
    );
    return option ? option.label : "Custom";
  }, [filter.startDate, filter.endDate, monthOptions]);

  const applyPeriod = (startDate: string, endDate: string) =>
    setFilter((prev) => ({ ...prev, startDate, endDate }));

  const handleYearChange = (value: string) => {
    const year = Number(value);
    if (!value || Number.isNaN(year)) return;
    if (year < MIN_SELECTABLE_YEAR || year > MAX_SELECTABLE_YEAR) return;

    const currentIdx = monthOptions.findIndex(
      (opt) =>
        opt.startDate === filter.startDate && opt.endDate === filter.endDate,
    );
    setSelectedYear(year);
    const periods = generateMonthOptionsForYear(year);
    const next = (currentIdx >= 0 && periods[currentIdx]) || periods[0];
    applyPeriod(next.startDate, next.endDate);
  };

  const handlePeriodChange = (value: string) => {
    const option = monthOptions.find((opt) => opt.label === value);
    if (option) applyPeriod(option.startDate, option.endDate);
  };

  const goToCurrentPeriod = () => {
    const period = getCurrentPeriod();
    setSelectedYear(new Date(period.startDate).getFullYear());
    applyPeriod(period.startDate, period.endDate);
  };

  const fetchOverview = useCallback(async () => {
    if (!userId) return;
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        userId,
        startDate: filter.startDate || "",
        endDate: filter.endDate || "",
      }).toString();
      setOverview(await PayslipService.getOverview(params));
    } catch (err) {
      ThrowError(err);
      setOverview(null);
    } finally {
      setIsLoading(false);
    }
  }, [userId, filter.startDate, filter.endDate]);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  const handleGeneratePayslip = async () => {
    if (!userId) return showToastNotification("danger", "User ID is missing.");
    if (!filter.startDate || !filter.endDate)
      return showToastNotification(
        "danger",
        "Please select a valid salary period.",
      );

    setIsGeneratingPdf(true);
    try {
      const arrayBuffer = await PayslipService.generatePayslip(
        userId,
        filter.startDate,
        filter.endDate,
      );
      if (!arrayBuffer)
        return showToastNotification("danger", "Failed to generate payslip.");

      const blob = new Blob([arrayBuffer], { type: "application/pdf" });
      const file = new File(
        [blob],
        `payslip_${userId}_${filter.startDate}_${filter.endDate}.pdf`,
        {
          type: "application/pdf",
        },
      );
      setSelectedDoc(file);
      setShowPreview(true);
      showToastNotification("success", "Payslip generated successfully!");
    } catch (err) {
      ThrowError(err);
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const handleClosePreview = () => {
    setShowPreview(false);
    setSelectedDoc(null);
  };

  const attendanceItems = overview
    ? [
      {
        label: "Total Days",
        value: overview.data.attendanceSummary.totalDays,
        color: "border-slate-200 text-slate-700 bg-slate-50",
      },
      {
        label: "Present",
        value: overview.data.attendanceSummary.attendance.present,
        color: "border-emerald-200 text-emerald-700 bg-emerald-50/50",
      },
      {
        label: "Absent",
        value: overview.data.attendanceSummary.attendance.absent,
        color: "border-rose-200 text-rose-700 bg-rose-50/50",
      },
      {
        label: "Half Day",
        value: overview.data.attendanceSummary.attendance.halfDay,
        color: "border-amber-200 text-amber-700 bg-amber-50/50",
      },
      {
        label: "Leave",
        value: overview.data.attendanceSummary.attendance.leave,
        color: "border-blue-200 text-blue-700 bg-blue-50/50",
      },
      {
        label: "Week Off",
        value: overview.data.attendanceSummary.attendance.weekOff,
        color: "border-slate-200 text-slate-600 bg-white",
      },
      {
        label: "Holiday",
        value: overview.data.attendanceSummary.attendance.holiday,
        color: "border-purple-200 text-purple-700 bg-purple-50/50",
      },
      {
        label: "Paid Leave Days",
        value: overview.data.attendanceSummary.paidLeaveDays,
        color: "border-teal-200 text-teal-700 bg-teal-50/50",
      },
      {
        label: "Unpaid Leave Days",
        value: overview.data.attendanceSummary.unpaidLeaveDays,
        color: "border-orange-200 text-orange-700 bg-orange-50/50",
      },
      {
        label: "Absent Deduction",
        value: overview.data.attendanceSummary.absentDeductionDays,
        color: "border-red-200 text-red-700 bg-red-50/50",
      },
      {
        label: "Total Deduction",
        value: overview.data.attendanceSummary.totalDeductionDays,
        color: "border-rose-300 text-rose-800 bg-rose-100/50 font-medium",
      },
    ]
    : [];

  if (isLoading) {
    return (
      <Layout>
        <FullPageLoader />
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="space-y-8">
        <MainHeading title="My Payslip" isBackButtonVisible={true} />

        <div className="flex flex-wrap items-center justify-between gap-4 p-4 bg-white rounded-xl shadow-sm border border-slate-200">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-slate-600 whitespace-nowrap">
              Salary Period:
            </span>

            <input
              type="number"
              inputMode="numeric"
              value={selectedYear}
              min={MIN_SELECTABLE_YEAR}
              max={MAX_SELECTABLE_YEAR}
              onChange={(e) => handleYearChange(e.target.value)}
              className="w-[90px] border border-slate-200 rounded-md px-3 py-2 text-sm h-9 focus:outline-none focus:ring-2 focus:ring-primary/30"
              aria-label="Salary year"
            />

            <Select
              value={selectedPeriodLabel}
              onValueChange={handlePeriodChange}
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Select period" />
              </SelectTrigger>
              <SelectContent>
                {monthOptions.map((opt) => (
                  <SelectItem key={opt.label} value={opt.label}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              variant="outline"
              size="sm"
              onClick={goToCurrentPeriod}
              className="flex items-center gap-1.5"
            >
              <Calendar className="w-4 h-4" />
              Today
            </Button>
          </div>

          {overview && !isLoading && (
            <Button
              variant="default"
              size="sm"
              onClick={handleGeneratePayslip}
              disabled={isGeneratingPdf}
              className="flex items-center gap-1.5 bg-primary hover:bg-primary/90 shadow-sm"
            >
              {isGeneratingPdf ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <FileDown className="w-4 h-4" />
              )}
              {isGeneratingPdf ? "Generating..." : "Generate Payslip"}
            </Button>
          )}
        </div>

        {isLoading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-10 w-10 animate-spin text-slate-300" />
          </div>
        ) : overview ? (
          <div className="space-y-8">
            <Card className="border-0 shadow-md p-6 bg-linear-to-r from-primary/10 via-white to-white overflow-hidden relative rounded-2xl">
              <div className="absolute -right-10 -top-10 w-40 h-40 bg-primary/5 rounded-full blur-3xl"></div>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 relative z-10">
                <div className="flex items-center gap-5">
                  <div className="w-16 h-16 bg-linear-to-br from-primary to-primary/80 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-primary/20 shrink-0">
                    <User className="w-8 h-8" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-slate-800 tracking-tight">
                      {overview.data.user.name}
                    </h2>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="bg-white/80 px-2.5 py-1 rounded-md text-xs font-semibold text-slate-600 border border-slate-200 shadow-sm backdrop-blur-sm">
                        CODE: {overview.data.user.employeeCode}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
              <SummaryCard
                icon={Briefcase}
                label="Daily Wages"
                value={handleFormatAmount(overview.data.cards.dailyWages)}
                barGradient="from-blue-400 to-indigo-500"
                iconBg="bg-blue-50"
                iconColor="text-blue-600"
              />
              <SummaryCard
                icon={Banknote}
                label="Basic Salary"
                value={`₹${overview.data.salary.basic}`}
                barGradient="from-sky-400 to-blue-500"
                iconBg="bg-sky-50"
                iconColor="text-sky-600"
              />
              <SummaryCard
                icon={TrendingUp}
                label="Gross Salary"
                value={handleFormatAmount(overview.data.cards.grossSalary)}
                barGradient="from-emerald-400 to-teal-500"
                iconBg="bg-emerald-50"
                iconColor="text-emerald-600"
                labelColor="text-emerald-600"
                valueColor="text-emerald-700"
              />
              <SummaryCard
                icon={TrendingDown}
                label="Total Deductions"
                value={handleFormatAmount(
                  overview.data.deductionsBreakdown.total,
                )}
                barGradient="from-rose-400 to-red-500"
                iconBg="bg-rose-50"
                iconColor="text-rose-600"
                labelColor="text-rose-600"
                valueColor="text-rose-700"
              />
              <SummaryCard
                icon={Wallet}
                label="Net Pay"
                value={handleFormatAmount(
                  overview.data.cards.grossSalary -
                  overview.data.deductionsBreakdown.total,
                )}
                barGradient="from-primary to-primary/70"
                iconBg="bg-primary/10"
                iconColor="text-primary"
                labelColor="text-primary/80"
                valueColor="text-primary"
                emphasized
                className="col-span-2 sm:col-span-1"
              />
            </div>

            <Card className="border border-slate-200/60 shadow-sm p-6 rounded-2xl bg-white overflow-hidden relative">
              <div className="absolute right-0 top-0 w-64 h-64 bg-slate-50 rounded-full blur-3xl -z-10 opacity-50"></div>
              <h3 className="text-lg font-bold mb-6 flex items-center gap-2 text-slate-800">
                <div className="p-2 bg-slate-100 rounded-lg text-slate-600">
                  <Clock className="w-5 h-5" />
                </div>
                Attendance Summary
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 gap-3 text-sm">
                {attendanceItems.map((item) => (
                  <div
                    key={item.label}
                    className={`rounded-xl p-4 border ${item.color} hover:shadow-md transition-shadow flex flex-col justify-between h-full`}
                  >
                    <p className="text-xs font-medium opacity-80 mb-2">
                      {item.label}
                    </p>
                    <p className="font-bold text-2xl">{item.value}</p>
                  </div>
                ))}
              </div>
            </Card>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <Card className="border-0 shadow-sm p-4">
                <div className="flex items-center gap-2 mb-4">
                  <TrendingUp className="w-5 h-5 text-emerald-600" />
                  <h3 className="text-base font-semibold">Earnings</h3>
                </div>
                <BreakdownTable
                  rows={overview.data.allowancesBreakdown.rows.map((r) => ({
                    label: r.allowance,
                    isFixed: r.isFixed,
                    amount: r.amount,
                    isConditionBased: r.isConditionBased

                  }))}
                  columnLabel="Earnings"
                  emptyText="No allowances configured for this period."
                  totalLabel="Total Allowances"
                  totalColor="text-emerald-700"
                />
              </Card>
              <Card className="border-0 shadow-sm p-4">
                <div className="flex items-center gap-2 mb-4">
                  <Wallet className="w-5 h-5 text-rose-600" />
                  <h3 className="text-base font-semibold">Deductions</h3>
                </div>
                <BreakdownTable
                  rows={overview.data.deductionsBreakdown.rows.map((r) => ({
                    label: r.deduction,
                    isFixed: r.isFixed,
                    amount: r.amount,
                    // isConditionBased: r.isConditionBased

                  }))}
                  columnLabel="Deduction"
                  emptyText="No deductions configured for this period."
                  totalLabel="Total Deductions"
                  totalColor="text-rose-700"
                />
              </Card>
            </div>

            <Card className="border border-slate-200/60 shadow-sm p-6 rounded-2xl bg-white">
              <h3 className="text-lg font-bold mb-6 flex items-center gap-2 text-slate-800">
                <div className="p-2 bg-indigo-50 rounded-lg text-indigo-600">
                  <Calculator className="w-5 h-5" />
                </div>
                Salary Breakdown
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                {overview.data.salaryBreakdown.map((item, idx) => (
                  <div
                    key={idx}
                    className="bg-white rounded-xl p-5 border border-slate-100 hover:border-indigo-100 hover:shadow-lg shadow-sm transition-all duration-300 group relative overflow-hidden flex flex-col justify-between"
                  >
                    <div className="absolute top-0 right-0 w-20 h-20 bg-indigo-50 rounded-bl-full -z-10 group-hover:scale-150 transition-transform duration-500 opacity-50"></div>
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                        {item.label}
                      </p>
                      <p className="text-2xl font-extrabold text-slate-800 mb-1 group-hover:text-indigo-700 transition-colors">
                        {handleFormatAmount(item.monthlyAmount)}
                      </p>
                      <p className="text-xs font-medium text-slate-400 bg-slate-50 inline-block px-2 py-1 rounded mb-3">
                        Yearly:{" "}
                        <span className="text-slate-600 font-semibold">
                          {handleFormatAmount(item.yearlyAmount)}
                        </span>
                      </p>
                    </div>
                    <p className="text-xs text-slate-500 leading-relaxed border-t border-slate-100 pt-3">
                      {item.description}
                    </p>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        ) : (
          <div className="flex justify-center py-12">
            <div className="w-full max-w-md text-center">
              <div className="mx-auto w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                <Inbox className="w-10 h-10 text-slate-400" />
              </div>
              <h3 className="text-xl font-semibold text-slate-700 mb-2">
                No Payslip Data Available
              </h3>
              <p className="text-sm text-slate-500 max-w-xs mx-auto mb-6">
                We couldn't find any payslip records for the selected period.
                Try generating a new payslip or choose a different period.
              </p>
            </div>
          </div>
        )}

        <CommonModal
          title="Payslip Preview"
          show={showPreview}
          onClose={handleClosePreview}
          className="sm:max-w-3xl"
        >
          <DocumentPreview selectedDocument={selectedDoc} />
        </CommonModal>
      </div>
    </Layout>
  );
};

export default Payslip;
